import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, TrendingUp, TrendingDown, Minus, Target } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { ManualPredictionComparisonService } from '@/services/ManualPredictionComparisonService';
import { useQuery } from '@tanstack/react-query';

interface HonestSimulation {
  id: string;
  file_id: string;
  file_name: string;
  created_at: string;
  total_positions: number;
  processing_time_ms: number;
  accuracy: number;
}

interface PredictionResult {
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  confidence: number;
  winProbability: number;
  lossProbability: number;
  usedPatterns: number;
  patternsUsed: Array<{
    pattern: string;
    frequency: number;
    successRate: number;
    confidenceScore: number;
  }>;
  skipReason?: string;
}

export const ManualHonestSimulationPrediction: React.FC = () => {
  const [selectedSimulation, setSelectedSimulation] = useState<string>('');
  const [inputSequence, setInputSequence] = useState<string>('1,0,1,0,1,1,0,1');
  const [threshold, setThreshold] = useState<number>(52);
  const [predictionResult, setPredictionResult] = useState<PredictionResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch available honest backtest simulations
  const { data: simulations, isLoading: simulationsLoading } = useQuery({
    queryKey: ['honest-backtest-simulations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('honest_backtest_simulations')
        .select(`
          id,
          file_id,
          created_at,
          total_positions,
          processing_time_ms,
          accuracy,
          trading_files!inner(filename)
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching simulations:', error);
        throw error;
      }
      
      return data?.map(sim => ({
        id: sim.id,
        file_id: sim.file_id,
        file_name: (sim.trading_files as any)?.filename || 'Unknown',
        created_at: sim.created_at,
        total_positions: sim.total_positions,
        processing_time_ms: sim.processing_time_ms,
        accuracy: sim.accuracy
      })) as HonestSimulation[] || [];
    }
  });

  const handlePredict = async () => {
    if (!selectedSimulation || !inputSequence.trim()) {
      setError('Please select a simulation and enter an input sequence');
      return;
    }

    setIsLoading(true);
    setError('');
    setPredictionResult(null);

    try {
      const result = await ManualPredictionComparisonService.predictFromHonestSimulation(
        selectedSimulation,
        inputSequence,
        threshold / 100
      );
      
      setPredictionResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to make prediction');
    } finally {
      setIsLoading(false);
    }
  };

  const getPredictionIcon = (prediction: string) => {
    switch (prediction) {
      case 'WIN': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'LOSS': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getPredictionColor = (prediction: string) => {
    switch (prediction) {
      case 'WIN': return 'bg-green-100 text-green-800 border-green-200';
      case 'LOSS': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">Manual Honest Simulation Prediction</h3>
        <p className="text-muted-foreground">
          Test makeRealisticPrediction logic using patterns from honest backtest simulations
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Prediction Input
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="simulation">Select Honest Backtest Simulation</Label>
              <Select 
                value={selectedSimulation} 
                onValueChange={setSelectedSimulation}
                disabled={simulationsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={simulationsLoading ? "Loading..." : "Choose a simulation"} />
                </SelectTrigger>
                <SelectContent>
                  {simulations && simulations.length > 0 ? (
                    simulations.map((sim) => (
                      <SelectItem key={sim.id} value={sim.id}>
                        {sim.file_name} - {new Date(sim.created_at).toLocaleDateString()} 
                        ({sim.total_positions} positions, {sim.accuracy?.toFixed(1)}% accuracy)
                      </SelectItem>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No simulations available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="threshold">Win Threshold (%)</Label>
              <Input
                id="threshold"
                type="number"
                min="1"
                max="99"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                placeholder="52"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sequence">Input Sequence (comma-separated: 1,0,1,0...)</Label>
            <Input
              id="sequence"
              value={inputSequence}
              onChange={(e) => setInputSequence(e.target.value)}
              placeholder="1,0,1,0,1,1,0,1"
              className="font-mono"
            />
          </div>

          <Button 
            onClick={handlePredict} 
            disabled={isLoading || !selectedSimulation || !inputSequence.trim()}
            className="w-full"
          >
            {isLoading ? 'Predicting...' : 'Run Honest Prediction'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {predictionResult && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Prediction Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${getPredictionColor(predictionResult.prediction)}`}>
                    {getPredictionIcon(predictionResult.prediction)}
                    <span className="font-semibold">{predictionResult.prediction}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Prediction</p>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {predictionResult.winProbability.toFixed(1)}%
                  </div>
                  <p className="text-sm text-muted-foreground">Win Probability</p>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {predictionResult.confidence.toFixed(1)}%
                  </div>
                  <p className="text-sm text-muted-foreground">Confidence</p>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {predictionResult.usedPatterns}
                  </div>
                  <p className="text-sm text-muted-foreground">Patterns Used</p>
                </div>
              </div>

              {predictionResult.skipReason && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Skip Reason:</strong> {predictionResult.skipReason}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {predictionResult.patternsUsed.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top Patterns Used (Top-8)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {predictionResult.patternsUsed.map((pattern, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">#{index + 1}</Badge>
                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {pattern.pattern}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <div className="font-semibold">{pattern.frequency}</div>
                          <div className="text-muted-foreground">Freq</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold">{pattern.successRate.toFixed(1)}%</div>
                          <div className="text-muted-foreground">Success</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold">{pattern.confidenceScore.toFixed(1)}</div>
                          <div className="text-muted-foreground">Weight</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};